import { Timestamp } from 'firebase/firestore';
import { DateTime } from 'luxon';
import { toValue } from 'vue';

/**
 * Zone-aware conversions between Firestore Timestamps and wall-clock time strings.
 *
 * The design system owns the *mechanism* — reading and writing a time as it appears in
 * a given IANA zone — while the application owns the *policy* of which zone that is (e.g. a
 * user's timezone preference). The zone is therefore always passed in as an argument; this
 * module never reads it from a store. When `zone` is null/undefined every operation falls
 * back to the host's system zone, matching native `Date` behaviour.
 *
 * The zone is resolved on every conversion rather than once on setup, so a reactive source keeps
 * both the values a component derives and the timestamps it emits on the current zone.
 *
 * Implements design-system owned counterparts for methods from src/composables/time.js
 */

/**
 * @param {string|import('vue').Ref<string>|(() => string)} timezone - IANA zone name, or a
 *   reactive source of one. Resolved per conversion, so a computed that formats through these
 *   helpers re-runs when the zone changes.
 */
// eslint-disable-next-line import/prefer-default-export
export function useZonedTime(timezone) {
  const propToLibraryDate = (value) => {
    const jsDate = value instanceof Timestamp ? value.toDate() : new Date(value);
    return DateTime.fromJSDate(jsDate).setZone(toValue(timezone));
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {AdjustDateOptions} options
   * @return {Date}
   */
  const adjustDate = (date, options) => {
    return propToLibraryDate(date).set(options).toJSDate();
  };

  /**
   * Returns a date string in a similar format as native .toLocaleTimeString() method, can also be provided options to
   * change the format
   *
   * @param {Timestamp|Date|string} date
   * @param {Object=} opts
   * @param {string=} opts.locales
   * @param {Object=} opts.options
   * @return {string}
   */
  const getLocaleTimeString = (date, { locales = undefined, options = null } = {}) => {
    let customOptions = options;
    const paramsCheck = ['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits', 'timeStyle'];
    if (options && paramsCheck.every((param) => !options[param])) {
      customOptions = { hour: 'numeric', minute: 'numeric', second: 'numeric', ...customOptions };
    }
    return propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions || DateTime.TIME_WITH_SECONDS);
  };

  /**
   * Returns a date string in a similar format as native .toLocaleDateString() method, can also be provided options to
   * change the format
   *
   * @param {Timestamp|Date|string} date
   * @param {Object=} opts
   * @param {string=} opts.locales
   * @param {Object=} opts.options
   * @return {string}
   */
  const getLocaleDateString = (date, { locales = undefined, options = null } = {}) => {
    let customOptions = options;
    const paramsCheck = ['weekday', 'year', 'month', 'day', 'dateStyle'];
    if (options && paramsCheck.every((param) => !options[param])) {
      customOptions = { year: 'numeric', month: 'numeric', day: 'numeric', ...customOptions };
    }
    return propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions || DateTime.DATE_SHORT);
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getHours = (date) => {
    return propToLibraryDate(date).hour;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getMinutes = (date) => {
    return propToLibraryDate(date).minute;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {AddSubtractDateOptions} options
   * @return {Date}
   */
  const addToDate = (date, options) => {
    return propToLibraryDate(date).plus(options).toJSDate();
  };

  return {
    addToDate, getMinutes, getHours, adjustDate, getLocaleTimeString, getLocaleDateString,
  };
}
